import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Users, Workflow, Plus, Pencil, Trash2, ChevronUp, ChevronDown,
  X, RotateCcw, Save, Factory, Store, ShieldCheck, ArrowRightLeft,
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

const KIND = {
  in_house: { label: 'In-house', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  outsourced: { label: 'Outsourced', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
};
const TYPE_ICON = { customer: Users, stock: Store, repair: ShieldCheck, inter_branch: ArrowRightLeft };

const EMPTY_PARTY = { name: '', party_kind: 'in_house', skills: [], phone: '', email: '', notes: '' };

export default function OrderSetup() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('parties');

  // Parties
  const [parties, setParties] = useState([]);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_PARTY);
  const [saving, setSaving] = useState(false);

  // Route templates
  const [orderTypes, setOrderTypes] = useState([]);
  const [routes, setRoutes] = useState({});
  const [activeType, setActiveType] = useState('customer');
  const [savingRoute, setSavingRoute] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s, t, r] = await Promise.all([
        api.get('/order-config/parties'),
        api.get('/order-config/skills'),
        api.get('/order-config/order-types'),
        api.get('/order-config/route-templates'),
      ]);
      setParties(p.data); setSkills(s.data); setOrderTypes(t.data); setRoutes(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load order setup');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // ---- party actions ----
  const openCreate = () => { setEditTarget(null); setForm(EMPTY_PARTY); setFormOpen(true); };
  const openEdit = (p) => {
    setEditTarget(p);
    setForm({ name: p.name || '', party_kind: p.party_kind, skills: p.skills || [], phone: p.phone || '', email: p.email || '', notes: p.notes || '' });
    setFormOpen(true);
  };
  const toggleSkill = (sk) => setForm(f => ({ ...f, skills: f.skills.includes(sk) ? f.skills.filter(x => x !== sk) : [...f.skills, sk] }));

  const submitParty = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editTarget) { await api.put(`/order-config/parties/${editTarget.id}`, form); toast.success('Party updated'); }
      else { await api.post('/order-config/parties', form); toast.success('Party added'); }
      setFormOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };
  const toggleActive = async (p) => {
    try { await api.patch(`/order-config/parties/${p.id}/toggle-active`); await load(); }
    catch (e) { toast.error('Failed'); }
  };
  const deleteParty = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/order-config/parties/${p.id}`); toast.success('Deleted'); await load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  // ---- route template actions ----
  const cps = routes[activeType] || [];
  const setCps = (next) => setRoutes(r => ({ ...r, [activeType]: next }));
  const move = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= cps.length) return;
    const next = [...cps]; [next[i], next[j]] = [next[j], next[i]]; setCps(next);
  };
  const removeCp = (i) => setCps(cps.filter((_, idx) => idx !== i));
  const patchCp = (i, patch) => setCps(cps.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const addCp = () => {
    const name = window.prompt('Checkpoint name (e.g. Enamel)'); if (!name) return;
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    setCps([...cps, { key, name: name.trim(), mode: 'sequential', qc: false, gate: false, owner_role: null }]);
  };
  const saveRoute = async () => {
    setSavingRoute(true);
    try { await api.put(`/order-config/route-templates/${activeType}`, { checkpoints: cps }); toast.success('Route saved'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSavingRoute(false); }
  };
  const resetRoute = async () => {
    if (!window.confirm('Reset this route to the default?')) return;
    try { const res = await api.post(`/order-config/route-templates/${activeType}/reset`); setCps(res.data.checkpoints); toast.success('Reset to default'); }
    catch (e) { toast.error('Reset failed'); }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] text-white px-4 py-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-full hover:bg-white/20" data-testid="back-btn"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold leading-tight">Order Setup</h1>
            <p className="text-xs text-emerald-200">Parties & route templates for the order engine</p>
          </div>
        </div>
        <div className="flex gap-1.5 mt-3 bg-emerald-800/40 rounded-full p-1">
          <button onClick={() => setTab('parties')} data-testid="tab-parties"
            className={`flex-1 py-1.5 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${tab === 'parties' ? 'bg-white text-[#075E54]' : 'text-emerald-100'}`}>
            <Users className="w-3.5 h-3.5" /> Party Master
          </button>
          <button onClick={() => setTab('routes')} data-testid="tab-routes"
            className={`flex-1 py-1.5 rounded-full text-xs font-semibold flex items-center justify-center gap-1 ${tab === 'routes' ? 'bg-white text-[#075E54]' : 'text-emerald-100'}`}>
            <Workflow className="w-3.5 h-3.5" /> Route Templates
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>
      ) : tab === 'parties' ? (
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
          <button onClick={openCreate} data-testid="add-party-btn" className="w-full flex items-center justify-center gap-2 bg-[#075E54] text-white font-semibold py-2.5 rounded-xl text-sm">
            <Plus className="w-4 h-4" /> Add Karigar / Vendor
          </button>
          {parties.length === 0 ? (
            <div className="text-center py-12 text-gray-400"><Factory className="w-12 h-12 mx-auto mb-2 opacity-30" /><p className="text-sm">No parties yet. Add your karigars and vendors.</p></div>
          ) : parties.map(p => (
            <div key={p.id} data-testid={`party-${p.id}`} className={`rounded-xl border p-3 bg-white ${p.is_active === false ? 'opacity-50' : ''}`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-gray-800">{p.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border font-semibold ${KIND[p.party_kind]?.cls}`}>{KIND[p.party_kind]?.label}</span>
                    {p.is_active === false && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-500 border border-red-200">Inactive</span>}
                  </div>
                  {p.skills?.length > 0 && <div className="flex gap-1 flex-wrap mt-1">{p.skills.map(s => <span key={s} className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{s}</span>)}</div>}
                  {p.phone && <p className="text-[11px] text-gray-400 mt-1 font-mono">{p.phone}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(p)} data-testid={`edit-party-${p.id}`} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-emerald-600 rounded-full hover:bg-emerald-50"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => toggleActive(p)} data-testid={`toggle-party-${p.id}`} className={`w-11 h-6 rounded-full relative ${p.is_active !== false ? 'bg-emerald-500' : 'bg-gray-300'}`}><span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${p.is_active !== false ? 'left-5' : 'left-0.5'}`} /></button>
                  <button onClick={() => deleteParty(p)} data-testid={`delete-party-${p.id}`} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-600 rounded-full hover:bg-red-50"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6">
          <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-2">
            {orderTypes.map(t => {
              const Icon = TYPE_ICON[t.key] || Workflow;
              return (
                <button key={t.key} onClick={() => setActiveType(t.key)} data-testid={`route-type-${t.key}`}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border flex items-center gap-1 ${activeType === t.key ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                  <Icon className="w-3.5 h-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
          {(() => { const t = orderTypes.find(x => x.key === activeType); return t ? (
            <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-2.5 text-[11px] text-emerald-800 mb-3">
              <b>Entry:</b> {t.entry}<br /><b>Exit:</b> {t.exit}
            </div>) : null; })()}

          <div className="space-y-1.5">
            {cps.map((c, i) => (
              <div key={i} data-testid={`checkpoint-${activeType}-${i}`} className="bg-white border border-gray-100 rounded-lg p-2.5">
                <div className="flex items-center gap-2">
                  <span className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                  <span className="flex-1 font-medium text-sm text-gray-800">{c.name}</span>
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} className="text-gray-300 hover:text-gray-600"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} className="text-gray-300 hover:text-gray-600"><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => removeCp(i)} className="text-gray-300 hover:text-red-500"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex gap-1.5 flex-wrap mt-2 ml-8">
                  <button onClick={() => patchCp(i, { mode: c.mode === 'parallel' ? 'sequential' : 'parallel' })}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${c.mode === 'parallel' ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                    {c.mode === 'parallel' ? 'Parallel' : 'Sequential'}
                  </button>
                  <button onClick={() => patchCp(i, { qc: !c.qc })}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${c.qc ? 'bg-green-100 text-green-700 border-green-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>QC</button>
                  <button onClick={() => patchCp(i, { gate: !c.gate })}
                    className={`text-[10px] px-2 py-0.5 rounded-full border ${c.gate ? 'bg-purple-100 text-purple-700 border-purple-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>Gate</button>
                  {c.owner_role && <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">{c.owner_role}</span>}
                </div>
              </div>
            ))}
          </div>

          <div className="flex gap-2 mt-3">
            <button onClick={addCp} data-testid="add-checkpoint-btn" className="flex-1 flex items-center justify-center gap-1 border border-dashed border-emerald-300 text-emerald-700 py-2 rounded-lg text-sm"><Plus className="w-4 h-4" /> Add step</button>
            <button onClick={resetRoute} className="px-3 border border-gray-200 text-gray-500 py-2 rounded-lg text-sm flex items-center gap-1"><RotateCcw className="w-4 h-4" /> Reset</button>
          </div>
          <button onClick={saveRoute} disabled={savingRoute} data-testid="save-route-btn" className="w-full mt-2 bg-[#075E54] text-white font-bold py-3 rounded-xl disabled:opacity-60 flex items-center justify-center gap-2">
            <Save className="w-4 h-4" /> {savingRoute ? 'Saving…' : 'Save Route'}
          </button>
        </div>
      )}

      {/* Party form modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => !saving && setFormOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full bg-white rounded-t-3xl px-5 pt-5 pb-10 space-y-3 max-w-lg mx-auto max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="party-form-modal">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-1" />
            <h2 className="font-bold text-gray-900 text-base">{editTarget ? 'Edit Party' : 'Add Karigar / Vendor'}</h2>
            <input placeholder="Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="party-name" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
            <div className="flex gap-2">
              {Object.entries(KIND).map(([k, v]) => (
                <button key={k} onClick={() => setForm(f => ({ ...f, party_kind: k }))} data-testid={`party-kind-${k}`}
                  className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border ${form.party_kind === k ? v.cls : 'bg-gray-50 text-gray-500 border-gray-200'}`}>{v.label}</button>
              ))}
            </div>
            <div>
              <label className="text-[11px] text-gray-500 font-semibold uppercase ml-1 mb-1 block">Skills</label>
              <div className="flex gap-1.5 flex-wrap">
                {skills.map(s => (
                  <button key={s} onClick={() => toggleSkill(s)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border ${form.skills.includes(s) ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}>{s}</button>
                ))}
              </div>
            </div>
            <input placeholder="Phone (optional)" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="party-phone" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
            <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm" />
            <button onClick={submitParty} disabled={saving} data-testid="save-party-btn" className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-60">{saving ? 'Saving…' : (editTarget ? 'Save Changes' : 'Add Party')}</button>
          </div>
        </div>
      )}
    </div>
  );
}
